'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import MobileBrandLabScene from './MobileBrandLabScene';

const MobileBrandLabHero = () => {
    // --- Dynamic Data Simulation State ---
    const [rankings, setRankings] = useState([
        { id: 1, label: 'SEO Specialist', rank: 2 },
        { id: 2, label: 'Brand Expert', rank: 5 },
        { id: 3, label: 'Local Search', rank: 8 },
    ]);

    const [gmbStats, setGmbStats] = useState({
        score: 96,
        calls: 24,
        views: 45,
        clicks: 18
    });

    const [currentReview, setCurrentReview] = useState("Excellent Service!");
    const [isReviewVisible, setIsReviewVisible] = useState(true);
    const reviewOptions = ["Excellent Service!", "Top Rated Advice", "Fast Results", "Highly Recommended", "Trustworthy Team"];

    // Data Simulation Effect
    useEffect(() => {
        const interval = setInterval(() => {
            // 1. Update Rankings
            setRankings(prev => {
                const next = prev.map(item => {
                    if (item.rank > 1 && Math.random() > 0.6) {
                        return { ...item, rank: item.rank - 1 };
                    }
                    return item;
                });
                return [...next].sort((a, b) => a.rank - b.rank);
            });

            // 2. Update Stats
            setGmbStats(prev => ({
                score: Math.min(100, prev.score + (Math.random() > 0.8 ? 1 : 0)),
                calls: prev.calls + (Math.random() > 0.7 ? 1 : 0),
                views: prev.views + (Math.random() > 0.5 ? 1 : 0),
                clicks: prev.clicks + (Math.random() > 0.8 ? 1 : 0),
            }));
        }, 2000);

        // Separate interval for Review Switching with Fade
        const reviewInterval = setInterval(() => {
            setIsReviewVisible(false); // Fade Out
            setTimeout(() => {
                setCurrentReview(prev => {
                    const idx = reviewOptions.indexOf(prev);
                    return reviewOptions[(idx + 1) % reviewOptions.length];
                });
                setIsReviewVisible(true); // Fade In
            }, 300); // Wait for fade out
        }, 3000); // 3 Seconds

        return () => {
            clearInterval(interval);
            clearInterval(reviewInterval);
        };
    }, []);

    return (
        <section className="relative w-full pt-32 pb-12 px-5 overflow-hidden min-h-[90vh] flex flex-col bg-white">

            <div className="absolute inset-x-0 top-0 h-[60vh] w-full z-0 pointer-events-none opacity-100 mask-radial-fade">
                <MobileBrandLabScene />
            </div>

            <div className="relative z-10 flex flex-col items-center w-full mt-auto">

                {/* Content */}
                <div className="flex flex-col gap-6 text-center w-full max-w-md mx-auto mb-8">

                    <h1 className="text-4xl font-bold tracking-tight text-slate-900 leading-[1.15]">
                        Elevate Your <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 animate-gradient-x bg-[length:200%_auto]">
                            Brand Visibility
                        </span>
                    </h1>

                    <p className="text-base text-slate-600 leading-relaxed font-light px-2">
                        Transform your online presence with <strong>our team's</strong> insider knowledge of Google Business Profile optimization.
                    </p>

                    <div className="flex flex-col gap-3 mt-2 w-full">
                        <Link
                            href="/contact"
                            className="w-full px-8 py-3.5 bg-slate-900 hover:bg-indigo-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-indigo-900/10 flex items-center justify-center gap-2"
                        >
                            Book Free Brand Audit
                            <Icon name="ArrowRightIcon" className="w-4 h-4" />
                        </Link>
                        <Link
                            href="#measurable-results"
                            className="w-full px-8 py-3.5 bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-900 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            View Success Stories
                        </Link>
                    </div>

                    {/* Compact Stats Row */}
                    <div className="mt-6 pt-6 border-t border-slate-100 flex justify-between gap-4 px-2">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-slate-900">50+</div>
                            <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Optimized</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-slate-900">3.5x</div>
                            <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Traffic</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-slate-900">92%</div>
                            <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Retention</div>
                        </div>
                    </div>
                </div>

                {/* Mobile Cards Stack (Simplified) */}
                <div className="w-full max-w-md mx-auto space-y-4">
                    {/* Card 1: Ranking Booster */}
                    <div className="w-full bg-white/80 backdrop-blur-xl p-4 rounded-xl shadow-lg border border-white/50">
                        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-md bg-indigo-100 flex items-center justify-center text-indigo-600">
                                    <Icon name="ChartBarIcon" className="w-3.5 h-3.5" />
                                </div>
                                <h3 className="text-xs font-bold text-slate-900">Ranking Surge</h3>
                            </div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Live</p>
                        </div>
                        <div className="space-y-2">
                            {rankings.slice(0, 2).map((item) => (
                                <div key={item.id} className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600 font-medium">{item.label}</span>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 ${item.rank === 1 ? 'text-green-600 bg-green-50' : 'text-slate-600 bg-slate-100'}`}>
                                        #{item.rank} <span className="text-[8px] text-green-500">▲</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Card 2: Interactive GMB Score */}
                    <div className="w-full bg-white/80 backdrop-blur-xl p-4 rounded-xl shadow-lg border border-white/50 flex items-center justify-between gap-4">
                        <div>
                            <div className="text-xs text-slate-500">GMB Health Score</div>
                            <div className="text-2xl font-bold text-slate-900">{Math.floor(gmbStats.score)}%</div>
                        </div>
                        <div className="flex-1 max-w-[120px]">
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className="bg-green-500 h-full rounded-full transition-all duration-1000"
                                    style={{ width: `${gmbStats.score}%` }}
                                ></div>
                            </div>
                            <div className="flex justify-between mt-1">
                                <span className="text-[8px] text-slate-400">Calls ↑</span>
                                <span className="text-[8px] text-slate-400">Views ↑</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <style jsx>{`
                .mask-radial-fade {
                    mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
                    -webkit-mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
                }
            `}</style>
        </section>
    );
};

export default MobileBrandLabHero;
